import styled from "styled-components";
import {
  ArrowUturnLeftIcon,
  IconButton,
  Tooltip,
  TrashIcon,
  Typography,
  useInterstateToken
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import type { FeeWithUIState } from "../types";
import { formatCurrency } from "../utils/format";

const RowContainer = styled.div<{
  $disabled?: boolean;
}>`
  display: grid;
  grid-template-columns: 1fr 220px 150px 48px;
  gap: 8px;
  align-items: center;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  background-color: transparent;
  border-bottom: 1px solid #d1d5db;
  min-height: 48px;
  opacity: ${props => (props.$disabled ? 0.5 : 1)};
  transition: opacity 0.2s;

  @media (max-width: 768px) {
    min-width: 450px;
  }
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
`;

const TypeCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AmountCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const ActionCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const getTypeLabel = (type: FeeWithUIState["type"]): string => {
  if (type === "NEW") return String(Locale.getText("FEEMANAGER_TYPE_NEW"));
  if (type === "USED") return String(Locale.getText("FEEMANAGER_TYPE_USED"));
  return String(Locale.getText("FEEMANAGER_TYPE_ALL"));
};

export interface FeeRowProps {
  disabled: boolean;
  fee: FeeWithUIState;
  locale?: string;
  onDelete: (feeId: string) => void;
  onOpenEdit: (feeId: string) => void;
  onRestore: (feeId: string) => void;
}

export const FeeRow = ({
  disabled,
  fee,
  locale = "en-US",
  onDelete,
  onOpenEdit,
  onRestore
}: FeeRowProps) => {
  const getToken = useInterstateToken();
  const errorColor =
    getToken("sem.color.on-surface.error.default") ?? "#c5281c";

  const handleRowClick = () => {
    if (!disabled) {
      onOpenEdit(fee.id);
    }
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      if (fee.removed) {
        onRestore(fee.id);
      } else {
        onDelete(fee.id);
      }
    }
  };

  return (
    <RowContainer $disabled={disabled} onClick={handleRowClick}>
      {/* Name */}
      <NameCell>
        <Typography variant={"body-md"}>{fee.name}</Typography>
      </NameCell>

      {/* Type (read-only) */}
      <TypeCell>
        <Typography variant={"body-md"}>{getTypeLabel(fee.type)}</Typography>
      </TypeCell>

      {/* Amount */}
      <AmountCell>
        <Typography variant={"body-md"}>
          {formatCurrency(fee.amount, locale)}
        </Typography>
      </AmountCell>

      {/* Delete / Restore */}
      <ActionCell>
        <Tooltip
          position="top"
          size="small"
          toolTipContent={fee.removed ? "Restore" : "Delete"}
        >
          <IconButton
            aria-label={fee.removed ? "Restore" : "Delete"}
            buttonStyle="tertiary"
            disabled={disabled}
            size="sm"
            onClick={handleActionClick}
          >
            {fee.removed ? (
              <ArrowUturnLeftIcon title="Restore" />
            ) : (
              <TrashIcon style={{ color: errorColor }} title="Delete" />
            )}
          </IconButton>
        </Tooltip>
      </ActionCell>
    </RowContainer>
  );
};
